import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChecklistEntry, ChecklistCategory, InspectionItem, ReportLanguage } from '../../types';
import { CHECKLIST_CATEGORIES, mergeChecklistEntries, updateChecklistEntry, setAllChecklistRelevance, isVirtualId } from '../../services/checklistLogic';
import { analyzeSigePlan } from '../../services/geminiService';
import { freimeldeDatum } from '../../services/inspectionItemStatus';
import { SaveStatus } from '../../hooks/useOfflineSync';
import { registriereZurueck } from '../../services/appUmfang';
import { SyncBadge } from '../common/SyncBadge';
import { nioMangelVorlage } from './checklistLogik';
import { kategorieText, pruefpunktText } from '../../services/checklistSprache';
import { Check, AlertTriangle, ChevronRight, Undo2, ListChecks, Footprints, Plus, Trash2, Sparkles, Loader2, X, CheckCircle2 } from 'lucide-react';

/**
 * Anzeigestatus: SaveStatus plus 'read-only'. Der Lesemodus ist kein Zustand
 * der Offline-Queue (dort wird nie etwas eingereiht), aber der Nutzer braucht
 * an derselben Stelle ein dauerhaftes Signal, dass nichts gespeichert wird.
 */
type ViewSaveStatus = SaveStatus | 'read-only';

interface Props {
    entries: ChecklistEntry[];
    onChange: (entries: ChecklistEntry[]) => void;
    reportDate?: string;
    activeSectionId?: string | null;
    syncStatus?: ViewSaveStatus;
    /** Öffnet den Mobile-Wizard vorbefüllt, um aus einem n.i.O.-Punkt einen Mangel zu machen. */
    onCreateDefect?: (initial: Partial<InspectionItem>) => void;
    /**
     * Berichtssprache: Standard-Prüfpunkte und Kategorien englisch anzeigen
     * (checklistSprache) und Titelvorschlag „Nicht erfüllt: …" beim n.i.O.-Mangel.
     */
    language?: ReportLanguage;
}

const newId = () => crypto.randomUUID();

export const MobileChecklistMode: React.FC<Props> = ({ entries, onChange, reportDate, activeSectionId, syncStatus, onCreateDefect, language }) => {
    const [view, setView] = useState<'walk' | 'manage'>('walk');
    // Rundgang beim ersten unerledigten Punkt beginnen — so geht nach einem
    // Wizard-Ausflug (n.i.O. → Mangel) keine Position verloren.
    const [walkIndex, setWalkIndex] = useState(() => {
        const date = freimeldeDatum(reportDate);
        const relevant = mergeChecklistEntries(entries, activeSectionId).filter(i => i.isRelevant);
        const idx = relevant.findIndex(i => !(i.status && i.lastChecked === date));
        return idx === -1 ? relevant.length : idx;
    });
    const [comment, setComment] = useState('');
    const [undoState, setUndoState] = useState<{ entries: ChecklistEntry[]; index: number } | null>(null);
    const [nokItem, setNokItem] = useState<ChecklistEntry | null>(null);
    const [activeCategories, setActiveCategories] = useState<ChecklistCategory[]>(['Dokumente', 'Baustelleneinrichtung', 'Allgemein', 'Benutzerdefiniert', 'SiGe-Plan']);
    const [isAiLoading, setIsAiLoading] = useState(false);
    const [aiSuggestions, setAiSuggestions] = useState<{ text: string; category?: string }[] | null>(null);
    const [aiSelected, setAiSelected] = useState<Set<number>>(new Set());
    const aiInputRef = useRef<HTMLInputElement>(null);

    const merged = useMemo(() => mergeChecklistEntries(entries, activeSectionId), [entries, activeSectionId]);
    const relevantItems = useMemo(() => merged.filter(i => i.isRelevant), [merged]);
    const current = relevantItems[walkIndex];
    // Geprüft wurde auf der Begehung; muss wortgleich zu walkIndex oben sein,
    // sonst zeigt der Rundgang bereits geprüfte Punkte wieder als offen.
    const checkedDate = freimeldeDatum(reportDate);

    const firstOpenIndex = () => {
        const idx = relevantItems.findIndex(i => !(i.status && i.lastChecked === checkedDate));
        return idx === -1 ? relevantItems.length : idx;
    };

    const applyStatus = (item: ChecklistEntry, status: 'ok' | 'nok') => {
        setUndoState({ entries, index: walkIndex });
        const updates: Partial<ChecklistEntry> = { status, lastChecked: checkedDate };
        if (comment.trim()) updates.comment = comment.trim();
        onChange(updateChecklistEntry(entries, item, updates, activeSectionId, newId));
        setComment('');
        if (status === 'nok') {
            setNokItem({ ...item, ...updates });
        } else {
            setWalkIndex(i => i + 1);
        }
    };

    const skip = () => { setUndoState({ entries, index: walkIndex }); setComment(''); setWalkIndex(i => i + 1); };

    const undo = () => {
        if (!undoState) return;
        onChange(undoState.entries);
        setWalkIndex(undoState.index);
        setUndoState(null);
        setNokItem(null);
    };

    const closeNokPrompt = (createDefect: boolean) => {
        const item = nokItem;
        setNokItem(null);
        setWalkIndex(i => i + 1);
        // Titel „Nicht erfüllt: <Prüfpunkt>" statt des Sollsatzes (checklistLogik).
        if (createDefect && item && onCreateDefect) onCreateDefect(nioMangelVorlage(item, language));
    };

    const updateItem = (item: ChecklistEntry, updates: Partial<ChecklistEntry>) => {
        onChange(updateChecklistEntry(entries, item, updates, activeSectionId, newId));
    };

    const addCustomItem = () => {
        onChange([...entries, {
            id: newId(), text: '', category: 'Benutzerdefiniert', isRelevant: true,
            lastChecked: null, status: null, comment: '', isCustom: true,
            sectionId: (activeSectionId && activeSectionId !== 'all') ? activeSectionId : undefined,
        }]);
    };

    const deleteItem = (item: ChecklistEntry) => {
        if (isVirtualId(item.id)) return;
        if (window.confirm('Eintrag zurücksetzen/löschen?')) onChange(entries.filter(e => e.id !== item.id));
    };

    const handleAiUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setIsAiLoading(true);
        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const suggestions = await analyzeSigePlan(event.target?.result as string, file.type);
                setAiSuggestions(suggestions);
                setAiSelected(new Set(suggestions.map((_: unknown, i: number) => i)));
            } catch { alert('Fehler bei der Analyse'); }
            finally { setIsAiLoading(false); }
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const confirmAiImport = () => {
        if (!aiSuggestions) return;
        onChange([...entries, ...aiSuggestions
            .filter((_, i) => aiSelected.has(i))
            .map(s => ({
                id: newId(), text: s.text, category: 'SiGe-Plan' as ChecklistCategory, isRelevant: true,
                lastChecked: null, status: null, comment: '', isCustom: true,
                sectionId: (activeSectionId && activeSectionId !== 'all') ? activeSectionId : undefined,
            }))]);
        setAiSuggestions(null);
    };

    const doneCount = relevantItems.filter(i => i.status === 'ok' || i.status === 'nok').length;

    // Zurueck-Taste der App und Zurueck-Geste im Handy-Browser (appUmfang.registriereZurueck):
    // nur solange eine der beiden Vollbildflaechen offen ist — sie schliesst
    // dann wie ihr eigener Knopf (KI-Vorschlaege: Kreuz; n.i.O.: „Nur markieren
    // & weiter", der Punkt ist da schon gespeichert). Die Hooks stehen VOR dem
    // fruehen Return des KI-Overlays.
    const overlayOffen = aiSuggestions !== null || nokItem !== null;
    const zurueckTasteRef = useRef<() => void>(() => {});
    // Wurzel des offenen Overlays: ein fremder Dialog darueber hat Vorrang (appUmfang).
    const flaecheRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        zurueckTasteRef.current = () => {
            if (aiSuggestions) {
                // Ausgewaehlte Vorschlaege nicht wortlos verwerfen: die Taste
                // bleibt stehen, „Übernehmen" oder das Kreuz entscheiden.
                if (aiSelected.size > 0) return;
                setAiSuggestions(null);
            } else if (nokItem) closeNokPrompt(false);
        };
    });
    useEffect(() => {
        if (!overlayOffen) return;
        return registriereZurueck(() => zurueckTasteRef.current(), () => flaecheRef.current);
    }, [overlayOffen]);

    // ---- KI-Auswahl-Overlay ----
    if (aiSuggestions) {
        return (
            <div ref={flaecheRef} data-glass-view="mobil" className="fixed inset-0 z-[110] bg-slate-50 flex flex-col animate-in slide-in-from-bottom-10 duration-300">
                <div className="bg-gradient-to-r from-violet-600 to-indigo-600 p-4 flex justify-between items-center text-white shrink-0">
                    <div className="flex items-center gap-2"><Sparkles className="w-5 h-5" /><h3 className="font-bold">KI-Vorschläge</h3></div>
                    <button onClick={() => setAiSuggestions(null)} aria-label="Schließen" className="relative treffer-44"><X className="w-6 h-6" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {aiSuggestions.map((s, i) => (
                        <button key={i} onClick={() => { const next = new Set(aiSelected); if (next.has(i)) { next.delete(i); } else { next.add(i); } setAiSelected(next); }}
                            className={`w-full p-3 border-2 rounded-xl flex items-center gap-3 text-left ${aiSelected.has(i) ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white'}`}>
                            <div className={`w-5 h-5 shrink-0 rounded border-2 flex items-center justify-center ${aiSelected.has(i) ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300'}`}>{aiSelected.has(i) && <Check className="w-3 h-3" />}</div>
                            <span className="text-sm font-medium text-slate-800">{s.text}</span>
                        </button>
                    ))}
                </div>
                <div className="p-4 bg-white border-t pb-safe">
                    <button onClick={confirmAiImport} disabled={aiSelected.size === 0} className="w-full py-4 bg-blue-600 text-white rounded-2xl font-bold shadow-lg disabled:opacity-50">
                        {aiSelected.size} Punkte übernehmen
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div data-glass-view="checkliste-mobil" className="flex flex-col h-full animate-fade-in">
            {/* Kopf: Umschalter + Sync-Status */}
            <div className="flex items-center justify-between gap-2 mb-4">
                <div data-glass="umschalter" className="flex bg-slate-100 p-1 rounded-xl">
                    <button onClick={() => { setWalkIndex(firstOpenIndex()); setView('walk'); }} className={`relative treffer-44 px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5 ${view === 'walk' ? 'bg-white shadow text-blue-700' : 'text-slate-500'}`}>
                        <Footprints className="w-4 h-4" /> Rundgang
                    </button>
                    <button onClick={() => setView('manage')} className={`relative treffer-44 px-3 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5 ${view === 'manage' ? 'bg-white shadow text-blue-700' : 'text-slate-500'}`}>
                        <ListChecks className="w-4 h-4" /> Verwalten
                    </button>
                </div>
                <SyncBadge status={syncStatus} />
            </div>

            {view === 'walk' ? (
                relevantItems.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center gap-3 text-slate-400 p-8">
                        <ListChecks className="w-12 h-12" />
                        <p className="font-bold text-slate-500">Keine aktiven Prüfpunkte</p>
                        <p className="text-sm">Aktiviere unter „Verwalten" die Prüfpunkte für dieses Projekt.</p>
                        <button onClick={() => setView('manage')} className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold">Zur Verwaltung</button>
                    </div>
                ) : walkIndex >= relevantItems.length ? (
                    <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center p-8">
                        <div className="bg-emerald-100 p-5 rounded-full text-emerald-600"><CheckCircle2 className="w-12 h-12" /></div>
                        <p className="font-bold text-slate-700 text-lg">Rundgang abgeschlossen</p>
                        <p className="text-sm text-slate-500">{doneCount} von {relevantItems.length} Punkten geprüft.</p>
                        <div className="flex gap-3">
                            <button onClick={() => setWalkIndex(0)} className="px-4 py-3 bg-slate-100 rounded-xl text-sm font-bold text-slate-600">Nochmal durchgehen</button>
                            <button onClick={() => setView('manage')} className="px-4 py-3 bg-blue-600 text-white rounded-xl text-sm font-bold">Übersicht</button>
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 flex flex-col">
                        {/* Fortschritt */}
                        <div className="mb-3">
                            <div className="flex justify-between text-[11px] font-bold text-slate-400 mb-1">
                                <span>Punkt {walkIndex + 1} / {relevantItems.length}</span>
                                {undoState && (
                                    <button onClick={undo} className="relative treffer-44 flex items-center gap-1 text-blue-600"><Undo2 className="w-3 h-3" /> Rückgängig</button>
                                )}
                            </div>
                            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className="h-full bg-blue-500 transition-all" style={{ width: `${(walkIndex / relevantItems.length) * 100}%` }} />
                            </div>
                        </div>

                        {/* Aktueller Prüfpunkt */}
                        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex-1 flex flex-col">
                            <span className="text-[11px] uppercase tracking-wider font-bold text-slate-400">{kategorieText(current.category, language)}</span>
                            <h3 className="text-lg font-bold text-slate-800 mt-1 leading-snug">{pruefpunktText(current.text, language)}</h3>
                            {current.status && (
                                <span className={`mt-2 self-start px-2 py-0.5 rounded-full text-[11px] font-bold ${current.status === 'ok' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                                    Zuletzt: {current.status === 'ok' ? 'i.O.' : 'n.i.O.'}
                                </span>
                            )}
                            <textarea
                                value={comment}
                                onChange={e => setComment(e.target.value)}
                                rows={2}
                                placeholder="Bemerkung (optional)…"
                                className="mt-4 w-full p-3 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                            />
                            <div className="flex-1" />
                            <button onClick={skip} className="relative treffer-44 self-center mt-4 text-xs font-bold text-slate-400 flex items-center gap-1">
                                Überspringen <ChevronRight className="w-3 h-3" />
                            </button>
                        </div>

                        {/* Große Aktionsflächen */}
                        <div className="grid grid-cols-2 gap-3 mt-4 pb-safe">
                            <button onClick={() => applyStatus(current, 'nok')} className="py-5 bg-red-600 text-white rounded-2xl font-bold text-lg shadow-lg flex items-center justify-center gap-2 active:scale-95 transition-transform">
                                <AlertTriangle className="w-6 h-6" /> n.i.O.
                            </button>
                            <button onClick={() => applyStatus(current, 'ok')} className="py-5 bg-emerald-600 text-white rounded-2xl font-bold text-lg shadow-lg flex items-center justify-center gap-2 active:scale-95 transition-transform">
                                <Check className="w-6 h-6" /> i.O.
                            </button>
                        </div>
                    </div>
                )
            ) : (
                <div className="flex-1 flex flex-col">
                    {/* Verwalten: Filter + Aktionen */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                        {CHECKLIST_CATEGORIES.map(cat => (
                            <button key={cat} onClick={() => setActiveCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat])}
                                className={`px-2.5 py-1.5 rounded-full text-[11px] font-bold border ${activeCategories.includes(cat) ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-500 border-slate-200'}`}>
                                {kategorieText(cat, language)}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-2 mb-4">
                        <button onClick={addCustomItem} className="flex-1 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-600 flex items-center justify-center gap-1"><Plus className="w-4 h-4" /> Eigener Punkt</button>
                        <button onClick={() => aiInputRef.current?.click()} disabled={isAiLoading} className="flex-1 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-60">
                            {isAiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} KI-Scan
                        </button>
                        <input type="file" ref={aiInputRef} className="hidden" accept=".pdf,image/*" onChange={handleAiUpload} />
                    </div>
                    <div className="flex gap-4 mb-4">
                        <button onClick={() => onChange(setAllChecklistRelevance(entries, merged, activeCategories, true, activeSectionId, newId))} className="relative treffer-44 text-[11px] text-blue-600 font-bold uppercase">Alle aktivieren</button>
                        <button onClick={() => onChange(setAllChecklistRelevance(entries, merged, activeCategories, false, activeSectionId, newId))} className="relative treffer-44 text-[11px] text-slate-400 font-bold uppercase">Alle deaktivieren</button>
                    </div>

                    {/* Punkte-Liste */}
                    <div className="space-y-2 pb-24">
                        {merged.filter(i => activeCategories.includes(i.category)).map(item => (
                            <div key={item.id} className={`bg-white border rounded-xl p-3 ${item.isRelevant ? 'border-blue-200' : 'border-slate-200 opacity-60'}`}>
                                <div className="flex items-start gap-3">
                                    <button
                                        onClick={() => updateItem(item, { isRelevant: !item.isRelevant })}
                                        className={`relative treffer-44 w-11 h-7 shrink-0 rounded-full p-0.5 transition-colors ${item.isRelevant ? 'bg-blue-600' : 'bg-slate-200'}`}
                                        aria-label={item.isRelevant ? 'Punkt deaktivieren' : 'Punkt aktivieren'}
                                    >
                                        <div className={`w-6 h-6 bg-white rounded-full shadow transition-transform ${item.isRelevant ? 'translate-x-4' : ''}`} />
                                    </button>
                                    <div className="flex-1 min-w-0">
                                        {item.isCustom ? (
                                            <input value={item.text} onChange={e => updateItem(item, { text: e.target.value })} placeholder="Prüfpunkt beschreiben…"
                                                className="w-full text-sm font-bold text-slate-800 outline-none border-b border-transparent focus:border-blue-300 py-0.5" autoFocus={!item.text} />
                                        ) : (
                                            <p className="text-sm font-bold text-slate-800">{pruefpunktText(item.text, language)}</p>
                                        )}
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-[10px] uppercase font-bold text-slate-400">{kategorieText(item.category, language)}</span>
                                            {item.status && <span className={`text-[10px] font-bold ${item.status === 'ok' ? 'text-emerald-600' : 'text-red-600'}`}>{item.status === 'ok' ? 'i.O.' : 'n.i.O.'}</span>}
                                        </div>
                                    </div>
                                    {/* Eigene Spalte statt unter dem Textfeld: dort ragte die
                                        Trefferflaeche ins Feld, ein Tipp an dessen Rand loeschte. */}
                                    {!isVirtualId(item.id) && (item.isCustom || item.isRelevant) && (
                                        <button onClick={() => deleteItem(item)} className="shrink-0 w-11 h-11 -my-2 -mr-2 flex items-center justify-center text-slate-400" aria-label="Punkt löschen"><Trash2 className="w-4 h-4" /></button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* n.i.O.-Entscheidung: erst gespeichert, dann optional Mangel erfassen */}
            {nokItem && (
                <div ref={flaecheRef} data-glass-view="projekt-dialog" className="fixed inset-0 z-[115] bg-black/60 backdrop-blur-sm flex items-end justify-center animate-fade-in">
                    <div className="bg-white rounded-t-3xl w-full p-5 pb-safe space-y-3">
                        <div className="flex items-center gap-2 text-red-600 font-bold"><AlertTriangle className="w-5 h-5" /> Als n.i.O. gespeichert</div>
                        <p className="text-sm text-slate-500">{pruefpunktText(nokItem.text, language)}</p>
                        {onCreateDefect && (
                            <button onClick={() => closeNokPrompt(true)} className="w-full py-4 bg-red-600 text-white rounded-2xl font-bold shadow-lg">
                                Als Mangel erfassen (Foto, Firma, Frist)
                            </button>
                        )}
                        <button onClick={() => closeNokPrompt(false)} className="w-full py-4 bg-slate-100 rounded-2xl font-bold text-slate-600">
                            Nur markieren & weiter
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
